import { memo, useState } from "react";
import DOMPurify from "dompurify";
import { ImageOff, Image as ImageIcon, Shapes } from "lucide-react";
import { MathJax } from "better-react-mathjax";
import { normalizeImages, imageRefSummary, type ImageRef } from "./media";
import { missingFigureLabel } from "./image-fallback";
import { QB_SANITIZE } from "./problem-text";

/** An unresolvable reference, shown rather than dropped so the gap is visible. */
function UnresolvedRef({ text, title }: { text: string; title: string }) {
  return (
    <span className="qb-chip qb-unresolved" title={title}>
      <ImageOff className="h-3 w-3" />
      <span>{text}</span>
    </span>
  );
}

/** Walks the candidate list on error, then degrades to a visible chip. */
function CandidateImg({ entry }: { entry: Extract<ImageRef, { kind: "image" }> }) {
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const url = entry.candidates[idx];

  if (url === undefined) {
    return entry.diagram ? (
      <DiagramRef raw={entry.raw} state="missing" />
    ) : (
      <UnresolvedRef
        text={missingFigureLabel(entry.alt, entry.raw)}
        title={entry.raw}
      />
    );
  }

  return (
    <span className="relative inline-block">
      {!loaded && (
        <span className="absolute inset-0 animate-pulse rounded bg-muted" aria-hidden />
      )}
      <img
        key={url}
        src={url}
        alt={entry.alt || "Problem figure"}
        loading="lazy"
        decoding="async"
        className={`max-w-full rounded border bg-white transition-opacity ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        style={{
          maxHeight: 300,
          minWidth: loaded ? undefined : 96,
          minHeight: loaded ? undefined : 64,
        }}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setLoaded(false);
          setIdx((i) => i + 1);
        }}
      />
    </span>
  );
}

/** A Thinkverse figure spec. Informational, not a failure — no asset exists. */
function SpecRef({
  specType,
  summary,
  raw,
}: {
  specType: string;
  summary: string;
  raw: string;
}) {
  return (
    <span className="qb-chip qb-spec" title={raw}>
      <Shapes className="h-3 w-3" />
      <span>
        <strong>{specType}</strong>
        {summary ? ` — ${summary}` : ""}
      </span>
    </span>
  );
}

export type DiagramState = "deferred" | "loading" | "missing";

function DiagramRef({ raw, state }: { raw: string; state: DiagramState }) {
  if (state === "missing") {
    return <UnresolvedRef text="Diagram could not be loaded" title={raw} />;
  }
  return (
    <span className="qb-chip qb-spec" title={raw}>
      <Shapes className="h-3 w-3" />
      <span>
        <strong>diagram</strong>
        {state === "loading" ? " — loading…" : " — open the preview to see it"}
      </span>
    </span>
  );
}

/** The base policy plus table markup — keeps one URI allowlist, not two. */
const TABLE_SANITIZE_CONFIG = {
  ...QB_SANITIZE,
  ALLOWED_TAGS: [
    ...QB_SANITIZE.ALLOWED_TAGS,
    "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption",
  ],
  ALLOWED_ATTR: [...QB_SANITIZE.ALLOWED_ATTR, "colspan", "rowspan"],
};

/** Thinkverse stores some "images" as `{grid, html}` HTML tables. */
function HtmlBlock({ html }: { html: string }) {
  const clean = DOMPurify.sanitize(html, TABLE_SANITIZE_CONFIG);
  if (!clean) {
    return (
      <UnresolvedRef text="table markup empty after sanitize" title="(html)" />
    );
  }
  return (
    <MathJax dynamic>
      {/* span, not div: MathJax's own wrapper is a span */}
      <span
        className="qb-html-block block overflow-x-auto"
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    </MathJax>
  );
}

/**
 * `images: unknown` on purpose — a JSON string or bare object must not throw.
 *
 * memo matters: `normalizeImages` parses + regexes each entry, and these render
 * once per problem row, so without it every keystroke in the search box redid
 * the work for all 200 rows.
 */
export const ProblemImages = memo(function ProblemImages({
  images,
  diagrams,
}: {
  images: unknown;
  diagrams?: DiagramState;
}) {
  return <ProblemImageRefs refs={normalizeImages(images)} diagrams={diagrams} />;
});

/**
 * Renders already-normalized refs. SurveyRenderer uses this to show only the
 * refs it could not place inside the question.
 */
export const ProblemImageRefs = memo(function ProblemImageRefs({
  refs,
  diagrams = "missing",
}: {
  refs: ImageRef[];
  diagrams?: DiagramState;
}) {
  if (!refs.length) return null;

  const unresolved = refs.filter((r) => r.kind === "unresolved").length;

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-start gap-3">
        {refs.map((r, i) =>
          r.kind === "image" ? (
            <CandidateImg key={i} entry={r} />
          ) : r.kind === "html" ? (
            <HtmlBlock key={i} html={r.html} />
          ) : r.kind === "spec" ? (
            <SpecRef key={i} specType={r.specType} summary={r.summary} raw={r.raw} />
          ) : r.kind === "diagram" ? (
            <DiagramRef key={i} raw={r.raw} state={diagrams} />
          ) : (
            <UnresolvedRef key={i} text={`${r.reason}: ${r.raw}`} title={r.raw} />
          ),
        )}
      </div>
      {unresolved > 0 && (
        <div className="text-[10px] font-medium text-amber-700">
          {unresolved} of {refs.length} image reference{refs.length === 1 ? "" : "s"} unresolved
        </div>
      )}
    </div>
  );
});

/** Keyed on resolvable refs, so it cannot claim an image that renders blank. */
export const ImageBadge = memo(function ImageBadge({ images }: { images: unknown }) {
  const s = imageRefSummary(images);
  if (s.total === 0) return null;

  // Specs are not failures, so only genuine unresolved refs turn the badge amber.
  const partial = s.unresolved > 0;
  const specOnly = s.specs > 0 && s.resolvable === 0 && s.unresolved === 0;
  if (s.diagrams > 0 && s.resolvable === 0 && s.unresolved === 0 && s.specs === 0) {
    return (
      <span
        className="flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs text-sky-700"
        title="EverTutor diagram — stored privately, rendered in the preview"
      >
        <Shapes className="h-3 w-3" />
        diagram
      </span>
    );
  }

  if (specOnly) {
    return (
      <span
        className="flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs text-sky-700"
        title={`${s.specs} figure spec${s.specs === 1 ? "" : "s"} — declarative, not a stored image`}
      >
        <Shapes className="h-3 w-3" />
        spec {s.specs}
      </span>
    );
  }

  return (
    <span
      className={`flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${
        partial
          ? "border-amber-200 bg-amber-50 text-amber-700"
          : "border-teal-200 bg-teal-50 text-teal-700"
      }`}
      title={
        partial
          ? `${s.resolvable} of ${s.total} image references resolve to our mirror`
          : `${s.total} image${s.total === 1 ? "" : "s"}`
      }
    >
      <ImageIcon className="h-3 w-3" />
      img {partial ? `${s.resolvable}/${s.total - s.specs - s.diagrams}` : s.resolvable || s.total}
    </span>
  );
});
